import { type SyntheticEvent, useEffect, useState } from "react";
import Navigation, { type NavigationOptionType, NavStep } from "../../../../shared-logic/utils/Navigation";
import { minimizeBuildBasket, parseByEquipmentAvailability } from "../../../utils/typedUtils";
import { objectToQueryString } from "../../../../../../common-deprecated/utils";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import { showBnbMonthlyRates } from "../../../utils/settings/bnbFinanceSettingUtils";
import type { ModelFilterGradeType, ModelGradeType } from "../../../../../../common-deprecated/types/CommonTypes";
import { isLexus } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { setStep } from "../../../redux/actions/NavigationActions";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const scrollToTop = (): void => {
    if (!document || !document.scrollingElement) return;
    document.scrollingElement.scrollTo(0, 0);
};

type StepUrlType = {
    href: string;
    onClick: (e: SyntheticEvent) => void;
};

const useStepUrl = (
    target?: NavStep,
    customNavProps?: Partial<NavigationOptionType>,
    noPageRefresh?: boolean,
    onClick?: (e: SyntheticEvent, url: string) => void,
): StepUrlType => {
    const basket = useBnbSelector((state) => state.basket);
    const car = useBnbSelector((state) => state.car);
    const grade = useBnbSelector((state) => state.grade);
    const model = useBnbSelector((state) => state.model);
    const modelFilter = useBnbSelector((state) => state.modelFilter);
    const finance = useBnbSelector((state) => state.finance);
    const navigation = useBnbSelector((state) => state.navigation);
    const utils = useBnbSelector((state) => state.utils);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const grades = useBnbSelector((state) => state.grades);
    const tradeInAemExternalData = useBnbSelector((state) => state.tradeInAemExternalData);
    const defaultSubmodel = useBnbSelector((state) => state.submodels?.list?.[0]);
    const isLexusSetting = isLexus(commonSettings);
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const [financeProductType, setFinanceProductType] = useState<string | null>(null);

    useEffect(() => {
        setFinanceProductType(finance.financeProductType);
    }, [finance.financeProductType]);

    // target is optional because this is a hook, also you cannot deeplink to buy and reserve step
    if (!target || target === NavStep.Buy || target === NavStep.Reserve) return { href: "", onClick: () => null };

    let navProps: Partial<NavigationOptionType>;
    let additionalQueryString: Record<string, any> = {};

    const step = navigation?.step;
    // Try to generate navProps automatically.
    switch (step) {
        case NavStep.Customize:
        case NavStep.Summary:
        case NavStep.Services:
        case NavStep.Buy:
        case NavStep.Reserve:
        case NavStep.Lease:
            navProps = {
                carId: car.id,
                modelId: car.model.id,
                bodyTypeId: car.body.id,
                gradeId: car.grade.id,
            };
            additionalQueryString = minimizeBuildBasket(basket, car, commonSettings);
            if (tradeInAemExternalData.data.quoteId && !commonSettings.query.quoteId) {
                additionalQueryString.quoteId = tradeInAemExternalData.data.quoteId;
            }
            break;

        case NavStep.Engine:
            navProps = {
                modelId: grade.model.id,
                bodyTypeId: grade.bodyType.id,
                gradeId: grade.id,
                carId: grade.defaultCarId, // Will most likely be overwritten.
            };
            // Propagate colour in engine step -- c is also used in minimizeBasket
            if (commonSettings.query.c) additionalQueryString = { c: commonSettings.query.c };
            break;

        case NavStep.Grades:
            navProps = {
                modelId: grades.modelId,
                subModelId: grades.submodelId,
                bodyTypeId: navigation.bodyType,
                carId: grades.list[0]?.defaultCarId,
            };
            break;

        case NavStep.Submodels:
            navProps = {
                modelId: model.id,
                subModelId: defaultSubmodel?.id,
                bodyTypeId: defaultSubmodel?.bodyType.id,
                carId: defaultSubmodel?.defaultCarId,
            };
            break;

        case NavStep.Model:
        default:
            if (
                [NavStep.Engine, NavStep.Customize, NavStep.Summary, NavStep.Services].includes(target) &&
                bnbSettings.modelHasActiveFilters
            ) {
                const ownGradeId = customNavProps ? customNavProps.gradeId : "";
                const filteredCar = (modelFilter.filteredGrades.find((filterGrade) => filterGrade.id === ownGradeId) ||
                    modelFilter.filteredGrades?.[0]) as ModelFilterGradeType | null;
                navProps = {
                    modelId: navigation.model,
                    bodyTypeId: filteredCar?.bodyType.id,
                    gradeId: filteredCar?.id,
                    carId: filteredCar?.carId,
                    filterIndex: String(filteredCar?.filterIndex),
                    // Renamed to selectedEquipment considering selectedOptions from Texus can actually contain accessories & options
                    selectedEquipment: filteredCar?.selectedOptions
                        .map((selectedOption) => selectedOption.shortId)
                        .join(","),
                };
            } else if (utils.filterEquipment) {
                const queryValues = utils.filterEquipment.split(":");
                const isSelected = queryValues[0] === "true";
                const equipmentId = queryValues[1];
                let gradesArray: ModelGradeType[] | null = null;

                if (equipmentId) {
                    gradesArray = parseByEquipmentAvailability<ModelGradeType>(model.grades, isSelected, equipmentId);
                }

                navProps = gradesArray
                    ? { modelId: model.id, carId: gradesArray[0]?.defaultCarId }
                    : { modelId: model.id, carId: model.defaultCarId };
            } else {
                navProps = model ? { modelId: model.id, carId: model.defaultCarId } : {};
            }
    }

    if (bnbSettings.preselectedEquipmentFilterId && utils.filterEquipment) {
        additionalQueryString.filterEquipment = utils.filterEquipment;
    }

    if (
        showBnbMonthlyRates(commonSettings, bnbSettings, car?.model?.internalCode, step, car) &&
        (utils.financeOption !== bnbSettings.defaultFinanceOption || commonSettings.query.financeOption)
    ) {
        additionalQueryString.financeOption = utils.financeOption;
    }

    // Any query that is in this array is sent to Navigation.build in order to re-render the CTA href's without the given disabledQueryStrings.
    const disabledQueryStrings: string[] = [];

    // When the financeProductType query is not present (anymore), it is still present in rendered CTA href's, we add it to disabledQueryStrings so that the CTA href's get re-rendered.
    if (financeProductType === "") {
        disabledQueryStrings.push("financeProductType");
    }

    const href = Navigation.build(
        commonSettings,
        target,
        { ...navProps, ...customNavProps } as NavigationOptionType, // customNavProps overwrite props
        objectToQueryString(additionalQueryString),
        disabledQueryStrings,
    );
    const customizeStepIndex = Navigation.getStepOrderIndex(NavStep.Customize, isLexusSetting);

    return {
        href,
        onClick: (event: SyntheticEvent<Element, Event>) => {
            const targetStepIndex = Navigation.getStepOrderIndex(target, isLexusSetting);
            if (noPageRefresh || (!!bnbSettings?.enableBuildSPA && targetStepIndex >= customizeStepIndex)) {
                event.preventDefault();
                dispatch(setStep(target));
                scrollToTop();
            }

            if (onClick) {
                onClick(event, href);
            }
        },
    };
};

export default useStepUrl;
